import { BaseAsyncAdapter } from './base-async.adapter';
import { Named } from '../base/named';
import { type ITridentResource } from '../../interfaces/ITridentResource';

export class NamedAsyncAdapter<T extends Named, U, D = unknown> extends BaseAsyncAdapter<T, U, D> {
    protected override async _toResource(named: T): Promise<U> {
        const aresource: Record<string, unknown> = {};
        aresource.name = this.context.identityProvider.identity(named);
        if (this.context.adapterConfig.referenceById) {
            aresource.objectId = Named.OBJECT_ID.templateValue(named) ?? Named.OBJECT_ID.value(named);
        }
        if (named.description) {
            this.properties(aresource).description = named.description;
        }
        return aresource as U;
    }

    protected override async _toModel(resource: U, named?: T): Promise<T> {
        if (!named) {
            throw new Error(' Cannot create abstract named');
        }

        const aresource: {
            objectId?: string;
            workspaceObjectId?: string;
            properties?: { description?: string; extraTridentInfo?: string };
        } = resource;
        named.name = this.context.identityProvider.name(aresource);
        if (this.context.adapterConfig.referenceById) {
            named.objectId = aresource?.objectId;
        }
        named.workspaceObjectId = aresource?.workspaceObjectId;
        if (aresource.properties) {
            if (aresource.properties.description) {
                named.description = aresource.properties.description;
            }
            if (aresource.properties.extraTridentInfo) {
                named.extraTridentInfo = JSON.parse(aresource.properties.extraTridentInfo);
            }
        }

        return named;
    }

    protected override async _toAzureResource(tridentResource: ITridentResource): Promise<U> {
        throw `Artifact type '${tridentResource.artifactType}' does not implement _toAzureResource method.`;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected properties(resource: { properties?: unknown }): Record<string, any> {
        const aresource = resource;
        if (!aresource.properties) {
            aresource.properties = {};
        }

        return aresource.properties;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected typeProperties(resource: { properties?: { typeProperties?: unknown } }): Record<string, any> {
        const properties = this.properties(resource);
        if (!properties.typeProperties) {
            properties.typeProperties = {};
        }
        return properties.typeProperties;
    }
}
